/* eslint-disable prettier/prettier */
import React, { lazy, useMemo, Suspense } from 'react';
import styled from 'styled-components/macro';
import clsx from 'clsx';

import { iconNames } from '@blueprint/icons';
import { rem, trackingId, forcedColorsMedia } from '@blueprint/ui';

import Box from '../Box';
import MissingIcon from './MissingIcon';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';

const IconWrapper = styled(Box)`
  ${forcedColorsMedia()`
    svg {
      color: CanvasText;
    }
  `}
`;

const getLazyIcon = (name) =>
  lazy(() =>
    import(
      /* webpackInclude: /\.js$/ */ /* webpackMode: "lazy-once" */ `@blueprint/icons/lib/esm/${name}`
    ),
  );

const useMemoIcon = (name) => useMemo(() => getLazyIcon(name), [name]);

/**
 * Icon from BRP
 */
const Icon = React.forwardRef(
  (
    {
      name,
      isActive,
      className,
      fill = 'currentColor',
      // Todo, need move to theme as standard
      svgProps = { color: '#333' },
      titleAccess,
      descAccess,
      component,
      size,
      cursor,
      ...rest
    },
    ref,
  ) => {
    const activeIcon = iconNames[`${name}Active`];
    const showActive = isActive && activeIcon;
    const iconSize = size ?? rem(18);

    /**
     * The original SVG Icon
     */
    const IconSvg = useMemoIcon(name);

    /**
     * The active SVG Icon
     */
    const ActiveIcon = useMemoIcon(activeIcon);

    return (
      <IconWrapper
        display="inline-flex"
        position="relative"
        flexShrink={0}
        ref={ref}
        // @ts-ignore
        as={component}
        className={clsx('Icon', className)}
        size={iconSize}
        data-testid={`Icon-${name}`}
        data-trackid={trackingId(`Icon-${name}`)}
        cursor={cursor}
        {...rest}
      >
        {/* 
          // @ts-ignore */}
        <ErrorBoundary fallback={<MissingIcon size={iconSize} />}>
          <Suspense fallback={<span />}>
            <IconSvg
              data-id="Icon"
              position="absolute"
              top={0}
              left={0}
              opacity={showActive ? 0 : 1}
              size={iconSize}
              fill={fill}
              titleAccess={titleAccess}
              descAccess={descAccess}
              {...svgProps}
            />
          </Suspense>
          {activeIcon && (
            <Suspense fallback={<span />}>
              <ActiveIcon
                data-id="ActiveIcon"
                position="absolute"
                opacity={showActive ? 1 : 0}
                top={0}
                left={0}
                size={iconSize}
                fill={fill}
                titleAccess={titleAccess}
                descAccess={descAccess}
                cursor={showActive ? 'pointer' : 'unset'}
                {...svgProps}
              />
            </Suspense>
          )}
        </ErrorBoundary>
      </IconWrapper>
    );
  },
);

Icon.displayName = 'Icon';

export default Icon;
